/**
 * SearchPageWithGrid — Search page with grid layout (no map variant).
 *
 * Single functional component replacing the previous dual class+functional pattern.
 * State management extracted into three hooks:
 *   - useSearchFilters: filter state, URL sync, sort
 *   - useDeepResearch: deep research streaming
 *   - useAiOverview: AI overview streaming
 */

import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import classNames from 'classnames';

import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { createResourceLocatorString } from '../../util/routes';
import { queryParamsToCategoryUrl, shouldRedirectToCategoryUrl } from '../../util/categoryUrl';
import { parse, stringify } from '../../util/urlHelpers';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
} from '../../util/search';
import {
  hasPermissionToViewData,
  isUserAuthorized,
  showCreateListingLinkForUser,
} from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { H3, H5, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
  getSearchPageResourceLocatorStringParams,
} from './SearchPage.shared';
import { filterCategoriesByVisibility } from '../../util/categoryVisibility';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import ActiveFiltersSummary from './ActiveFiltersSummary/ActiveFiltersSummary';
import AiFilterPanel from './AiFilterPanel/AiFilterPanel';
import AiIntentCard from './AiIntentCard/AiIntentCard';
import DeepResearchPanel from './DeepResearchPanel/DeepResearchPanel';
import AiSearchOverview from './AiSearchOverview/AiSearchOverview';
import CategoryNavStrip from './CategoryNavStrip/CategoryNavStrip';
import WhyPcific from './WhyPcific/WhyPcific';

import { setViewMode } from './SearchPage.duck';
import { selectAnnotations as selectAiAssistantAnnotations } from '../../ducks/aiAssistant.duck';
import { fetchCategoryVisibility } from '../../ducks/categoryVisibility.duck';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

import useSearchFilters from './hooks/useSearchFilters';
import useDeepResearch from './hooks/useDeepResearch';
import useAiOverview from './hooks/useAiOverview';

import css from './SearchPage.module.css';
import { getMobileSearchFilters } from './mobileSearchFilters';

const MODAL_BREAKPOINT = 768;
const FILTER_DROPDOWN_OFFSET = -14;

/**
 * SearchPageWithGrid — the main search page component.
 */
const SearchPageWithGrid = props => {
  const {
    listings = [],
    pagination,
    scrollingDisabled,
    searchInProgress,
    searchListingsError,
    searchParams = {},
    currentUser,
    onManageDisableScrolling,
    onUpdateWishlist,
    viewMode = 'grid',
    onSetViewMode,
    aiAssistantAnnotations = [],
    currentPageResultIds,
    categoryVisibility = { loaded: false, loading: false },
    onFetchCategoryVisibility,
  } = props;

  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const params = useParams();

  // ── Category URL redirect ────────────────────────────────────────
  useEffect(() => {
    if (location.pathname === '/s') {
      const queryParams = parse(location.search);
      if (shouldRedirectToCategoryUrl(queryParams)) {
        const categoryUrl = queryParamsToCategoryUrl(queryParams);
        if (categoryUrl) history.replace(categoryUrl);
      }
    }
  }, [location.pathname, location.search, history]);

  // ── Enhanced location for category pages ─────────────────────────
  const enhancedLocation = useMemo(() => {
    const { category1, category2, category3 } = params;
    if (category1) {
      const existingParams = parse(location.search);
      const categoryParams = {};
      if (category1) categoryParams.pub_categoryLevel1 = category1;
      if (category2) categoryParams.pub_categoryLevel2 = category2;
      if (category3) categoryParams.pub_categoryLevel3 = category3;
      const mergedParams = { ...existingParams, ...categoryParams };
      const newSearch = stringify(mergedParams);
      return { ...location, search: newSearch ? `?${newSearch}` : '' };
    }
    return location;
  }, [location, params]);

  // ── Custom hooks ─────────────────────────────────────────────────
  const filters = useSearchFilters({
    config,
    routeConfiguration,
    history,
    location: enhancedLocation,
    params,
    searchParams,
  });

  const research = useDeepResearch({
    location: enhancedLocation,
    history,
  });

  const aiOverview = useAiOverview({
    location: enhancedLocation,
    history,
    routeConfiguration,
    listings,
  });

  // ── Search interaction tracking (feeds neural model) ──────────────
  const clickedListingIdsRef = useRef(new Set());
  const visibleListingIdsRef = useRef([]);

  useEffect(() => {
    visibleListingIdsRef.current = (listings || []).map(l => l.id?.uuid || l.id);
    clickedListingIdsRef.current = new Set();
  }, [currentPageResultIds]);

  useEffect(() => {
    return () => {
      const clicked = clickedListingIdsRef.current;
      const visible = visibleListingIdsRef.current;
      const userId = currentUser?.id?.uuid || 'anonymous';
      const ignored = visible.filter(id => !clicked.has(id));
      if (ignored.length > 0) {
        try {
          const { logRecommendationEvent } = require('../../util/api');
          ignored.slice(0, 10).forEach(listingId => {
            logRecommendationEvent({
              userId,
              listingId,
              action: 'ignore',
              metadata: { source: 'search' },
            });
          });
        } catch (_) {}
      }
    };
  }, []);

  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'test' &&
      !categoryVisibility.loaded &&
      !categoryVisibility.loading &&
      onFetchCategoryVisibility
    ) {
      onFetchCategoryVisibility();
    }
  }, [categoryVisibility.loaded, categoryVisibility.loading, onFetchCategoryVisibility]);

  // ── Derived values ───────────────────────────────────────────────
  const currentPathParams = params || {};
  const { listingType: listingTypePathParam } = currentPathParams;
  const { listingFields } = config?.listing || {};
  const { defaultFilters: defaultFiltersRaw, sortConfig } = config?.search || {};
  const activeListingTypes = config?.listing?.listingTypes.map(c => c.listingType);
  const defaultFiltersConfig = listingTypePathParam
    ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
    : defaultFiltersRaw;

  const marketplaceCurrency = config.currency;
  const categoryConfiguration = config.categoryConfiguration;
  const listingCategories = categoryConfiguration.categories;
  const visibleListingCategories = filterCategoriesByVisibility(
    listingCategories,
    categoryVisibility
  );
  const listingFieldsConfig = pickListingFieldFilters({
    listingFields,
    locationSearch: enhancedLocation.search,
    categoryConfiguration,
    activeListingTypes,
    currentPathParams,
  });
  const filterConfigs = {
    listingFieldsConfig,
    defaultFiltersConfig,
    listingCategories,
    activeListingTypes,
    currentPathParams,
  };

  const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
    enhancedLocation.search,
    searchParams,
    filterConfigs,
    sortConfig,
    isOriginInUse(config)
  );
  const validQueryParams = urlQueryParams;
  const isKeywordSearch = isMainSearchTypeKeywords(config);

  // ── Available filters ────────────────────────────────────────────
  const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
    ['categoryLevel', 'listingType'].includes(f.key)
  );
  const builtInFilters = isKeywordSearch
    ? defaultFiltersConfig.filter(
        f => !['keywords', 'categoryLevel', 'listingType'].includes(f.key)
      )
    : defaultFiltersConfig.filter(f => !['categoryLevel', 'listingType'].includes(f.key));
  const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
    listingFieldsConfig,
    activeListingTypes
  );
  const availableFilters = [
    ...builtInPrimaryFilters,
    ...customPrimaryFilters,
    ...builtInFilters,
    ...customSecondaryFilters,
  ];
  const mobileFilters = getMobileSearchFilters(availableFilters);

  // ── Selected filters ─────────────────────────────────────────────
  const selectedFilters = validQueryParams;
  const isValidDatesFilter =
    searchParamsInURL.dates == null ||
    (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
  const keysOfSelectedFilters = Object.keys(selectedFilters);
  const selectedFiltersCountForMobile = isKeywordSearch
    ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
    : keysOfSelectedFilters.length;

  // ── Listings status ──────────────────────────────────────────────
  const hasPaginationInfo = !!pagination && pagination.totalItems != null;
  const hasLoadedListings = !searchInProgress && listings.length > 0;
  const totalItems =
    searchParamsAreInSync && hasPaginationInfo
      ? pagination.totalItems
      : pagination?.paginationUnsupported
      ? listings.length
      : hasLoadedListings
      ? listings.length
      : 0;
  const listingsAreLoaded =
    !searchInProgress &&
    (searchParamsAreInSync || hasLoadedListings) &&
    !!(hasPaginationInfo || pagination?.paginationUnsupported);

  const conflictingFilterActive = isAnyFilterActive(
    sortConfig.conflictingFilters,
    validQueryParams,
    filterConfigs
  );

  // ── Active filter entries (for pills & sidebar) ──────────────────
  const skipKeys = [
    'keywords', 'pub_listingType', 'sort', 'page',
    'mapSearch', 'origin', 'bounds', 'address',
    'pub_categoryLevel1', 'pub_categoryLevel2', 'pub_categoryLevel3',
  ];
  const activeFilterEntries = Object.entries(validQueryParams)
    .filter(([key, value]) => value != null && value !== '' && !skipKeys.includes(key))
    .map(([key, value]) => {
      const cleanKey = key.replace('pub_', '');
      const filterConfig = availableFilters.find(
        f => f.key === cleanKey || `pub_${f.key}` === key
      );
      const label = filterConfig?.label || cleanKey.replace(/_/g, ' ').replace(/Level\d+/gi, '');
      return {
        key,
        label,
        displayValue: typeof value === 'string' ? value.replace(/-/g, ' ') : String(value),
        onRemove: () => filters.getHandleChangedValueFn(true)({ [key]: null }),
      };
    });

  const headerActiveFilters = activeFilterEntries.map(f => ({
    key: f.key,
    label: `${f.label}: ${f.displayValue}`,
    onRemove: f.onRemove,
  }));

  // ── Condition filter handlers ────────────────────────────────────
  const currentCondition = searchParamsInURL.pub_condition_id || null;
  const onConditionChange = value => {
    filters.getHandleChangedValueFn(true)({ pub_condition_id: value || null });
  };

  // ── AI intent card ───────────────────────────────────────────────
  const hasAiOverview = aiOverview.aiOverviewActive || aiOverview.aiOverviewComplete;
  const aiIntentCardMaybe =
    isKeywordSearch && validQueryParams.keywords && !hasAiOverview ? (
      <AiIntentCard
        keywords={validQueryParams.keywords}
        listings={listings}
        totalItems={totalItems}
        searchInProgress={searchInProgress}
      />
    ) : null;

  // ── SortBy component ─────────────────────────────────────────────
  const sortBy = mode => {
    return sortConfig.active ? (
      <SortBy
        sort={validQueryParams[sortConfig.queryParamName]}
        isConflictingFilterActive={!!conflictingFilterActive}
        hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
        selectedFilters={selectedFilters}
        onSelect={filters.handleSortBy}
        showAsPopup
        mode={mode}
        labelId={`${mode}-search-page-sort-by`}
        contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
      />
    ) : null;
  };

  // ── Top categories for empty-state ───────────────────────────────
  const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);
  const topCategories = (listingCategories || [])
    .slice(0, 8)
    .map(cat => ({ id: cat.id, name: cat.name }));

  const noResultsInfo = (
    <NoSearchResultsMaybe
      listingsAreLoaded={listingsAreLoaded}
      totalItems={totalItems}
      location={enhancedLocation}
      resetAll={filters.resetAll}
      showCreateListingsLink={showCreateListingsLink}
      keywords={validQueryParams.keywords}
      topCategories={topCategories}
    />
  );

  // ── Page meta ────────────────────────────────────────────────────
  const pageHeading = searchInProgress
    ? intl.formatMessage({ id: 'MainPanelHeader.loadingResults' })
    : intl.formatMessage({ id: 'MainPanelHeader.foundResults' }, { count: totalItems });

  const { title, description, schema } = createSearchResultSchema(
    listings,
    searchParamsInURL || {},
    intl,
    routeConfiguration,
    config,
    pageHeading
  );

  const topbarClasses = filters.isMobileModalOpen
    ? classNames(css.topbarBehindModal, css.topbar)
    : css.topbar;

  // ── Category nav strip data ─────────────────────────────────────
  const activeCategory1 = params.category1 || null;
  const activeCategory2 = params.category2 || null;
  const activeCategory = activeCategory1
    ? listingCategories.find(c => c.id === activeCategory1) || null
    : null;

  // ── Category breadcrumbs (desktop indicator) ───────────────────
  const categoryBreadcrumbs = [];
  if (activeCategory1 && activeCategory) {
    categoryBreadcrumbs.push({
      label: activeCategory.name,
      clearRouteName: 'SearchPage',
      clearRouteParams: {},
    });
    if (activeCategory2) {
      const subcat = (activeCategory.subcategories || []).find(s => s.id === activeCategory2);
      if (subcat) {
        categoryBreadcrumbs.push({
          label: subcat.name,
          clearRouteName: 'CategoryPage1',
          clearRouteParams: { category1: activeCategory1 },
        });
      }
    }
  }

  // ── Merged annotations ───────────────────────────────────────────
  const mergedAnnotations = [
    ...research.researchAnnotations,
    ...aiOverview.aiOverviewAnnotations,
    ...aiAssistantAnnotations,
  ];

  // ── Render ───────────────────────────────────────────────────────
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      description={description}
      title={title}
      schema={schema}
    >
      <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />

      <div className={css.pageContainer}>
        {/* ── AI Overview (only when ai_query is present) ── */}
        <DeepResearchPanel
          isResearching={research.isResearching}
          steps={research.researchSteps}
          annotations={research.researchAnnotations}
          summary={research.researchSummary}
          confidence={research.researchConfidence}
          explanation={research.researchExplanation}
          error={research.researchError}
          phase={research.researchPhase}
          query={research.researchQuery}
          onDismiss={research.handleDismissResearch}
        />

        {hasAiOverview && (
          <AiSearchOverview
            isActive={aiOverview.aiOverviewActive}
            isComplete={aiOverview.aiOverviewComplete}
            error={aiOverview.aiOverviewError}
            intent={aiOverview.aiOverviewIntent}
            filters={aiOverview.aiOverviewFilters}
            excludedSummary={aiOverview.aiOverviewExcluded}
            content={aiOverview.aiOverviewContent}
            annotations={aiOverview.aiOverviewAnnotations}
            confidence={aiOverview.aiOverviewConfidence}
            explanation={aiOverview.aiOverviewExplanation}
            mode={aiOverview.aiOverviewMode}
            toolSteps={aiOverview.aiToolSteps}
            hasRespondStarted={aiOverview.hasRespondStarted}
            listings={listings}
            query={aiOverview.aiQuery || ''}
            onDismiss={aiOverview.handleDismissAiOverview}
            onPickClick={aiOverview.handleScrollToListing}
            onSuggestionClick={aiOverview.handleAiSuggestion}
            onQuickAction={aiOverview.handleQuickAction}
          />
        )}

        {/* ── Category navigation strip ── */}
        <CategoryNavStrip
          categories={visibleListingCategories}
          activeCategory1={activeCategory1}
          activeCategory2={activeCategory2}
          activeCategory={activeCategory}
          categoryVisibility={categoryVisibility}
        />

        {/* ── Mobile filters ── */}
        <SearchFiltersMobile
          className={css.searchFiltersMobileList}
          urlQueryParams={validQueryParams}
          sortByComponent={sortBy('mobile')}
          listingsAreLoaded={listingsAreLoaded}
          resultsCount={totalItems}
          searchInProgress={searchInProgress}
          searchListingsError={searchListingsError}
          showAsModalMaxWidth={MODAL_BREAKPOINT}
          onManageDisableScrolling={onManageDisableScrolling}
          onOpenModal={filters.onOpenMobileModal}
          onCloseModal={filters.onCloseMobileModal}
          resetAll={filters.resetAll}
          selectedFiltersCount={selectedFiltersCountForMobile}
          isMapVariant={false}
          noResultsInfo={noResultsInfo}
          location={enhancedLocation}
        >
          <AiFilterPanel
            onApplyFilters={f => filters.getHandleChangedValueFn(true)(f)}
            currentCategory={currentPathParams}
            currentFilters={validQueryParams}
            onClearFilters={e => filters.resetAll(e)}
            currentUser={currentUser}
          />
          {mobileFilters.map(filterConfig => {
            const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${filterConfig.key}`;
            const filterId = `SearchFiltersMobile.${filterConfig.key.toLowerCase()}`;
            return (
              <FilterComponent
                key={key}
                id={filterId}
                config={filterConfig}
                containerId="SearchPage_MobileFilters"
                listingCategories={visibleListingCategories}
                marketplaceCurrency={marketplaceCurrency}
                urlQueryParams={validQueryParams}
                initialValues={filters.getInitialValues()}
                getHandleChangedValueFn={filters.getHandleChangedValueFn}
                intl={intl}
                liveEdit
                showAsPopup={false}
              />
            );
          })}
        </SearchFiltersMobile>

        {/* ── Desktop controls bar ── */}
        <MainPanelHeader
          className={css.mainPanel}
          sortByComponent={sortBy('desktop')}
          isSortByActive={sortConfig.active}
          listingsAreLoaded={listingsAreLoaded}
          resultsCount={totalItems}
          searchInProgress={searchInProgress}
          searchListingsError={searchListingsError}
          noResultsInfo={noResultsInfo}
          viewMode={viewMode}
          onSetViewMode={onSetViewMode}
          keywords={validQueryParams.keywords}
          activeFilters={headerActiveFilters}
          onConditionChange={onConditionChange}
          currentCondition={currentCondition}
          aiIntentCard={aiIntentCardMaybe}
          onToggleFilters={filters.toggleFilters}
          filtersOpen={filters.filtersOpen}
          categoryBreadcrumbs={categoryBreadcrumbs}
        >
          {/* Active filters summary — between controls row and results */}
          <ActiveFiltersSummary
            filters={activeFilterEntries}
            onClearAll={e => filters.resetAll(e)}
          />
        </MainPanelHeader>

        {/* ── Sidebar + results grid ── */}
        <div className={css.contentLayout}>
          {/* ── Sidebar (desktop only) ── */}
          <aside className={css.sidebarColumn} data-testid="filterColumnAside">
            <div className={css.filterColumnContent}>
              {availableFilters.map(filterConfig => {
                const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${filterConfig.key}`;
                const filterId = `SearchFiltersDesktop.${filterConfig.key.toLowerCase()}`;
                return (
                  <FilterComponent
                    key={key}
                    id={filterId}
                    className={css.filter}
                    config={filterConfig}
                    containerId="SearchPageWithGrid_DesktopFilters"
                    listingCategories={visibleListingCategories}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={filters.getInitialValues()}
                    getHandleChangedValueFn={filters.getHandleChangedValueFn}
                    intl={intl}
                    liveEdit
                    showAsPopup={false}
                    isDesktop
                  />
                );
              })}
              <button className={css.resetAllButton} onClick={e => filters.resetAll(e)}>
                <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
              </button>
            </div>
          </aside>

          {/* ── Results column ── */}
          <div className={css.mainColumn}>
            {noResultsInfo}
            <div className={css.searchResultContainer}>
              <div
                className={classNames(css.listingsWrapper, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError) && !aiOverview.aiOverviewActive,
                })}
                data-ai-transition={
                  aiOverview.aiOverviewComplete && !(listingsAreLoaded || searchListingsError)
                    ? ''
                    : undefined
                }
              >
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                <SearchResultsPanel
                  className={css.searchListingsPanel}
                  listings={listings}
                  pagination={listingsAreLoaded ? pagination : null}
                  search={parse(enhancedLocation.search)}
                  isMapVariant={false}
                  currentUser={currentUser}
                  onUpdateFavorites={onUpdateWishlist}
                  listingTypeParam={listingTypePathParam}
                  intl={intl}
                  searchInProgress={searchInProgress}
                  viewMode={viewMode}
                  annotations={mergedAnnotations}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Section: Why PCIFIC ── */}
      <WhyPcific />

      <FooterContainer />
    </Page>
  );
};

// ── Redux connection (unchanged) ──────────────────────────────────

const mapStateToProps = state => {
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    viewMode,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    currentUser: state.user.currentUser,
    viewMode,
    aiAssistantAnnotations: selectAiAssistantAnnotations(state),
    currentPageResultIds,
    categoryVisibility: state.categoryVisibility,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onUpdateWishlist: payload => dispatch(updateProfile(payload)),
  onSetViewMode: mode => dispatch(setViewMode(mode)),
  onFetchCategoryVisibility: () => dispatch(fetchCategoryVisibility()),
});

// Named export for tests and other consumers
export { SearchPageWithGrid as EnhancedSearchPage };

const SearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(SearchPageWithGrid);

export default SearchPage;
